import { Link, useParams } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EventCard from "@/components/feature/EventCard";
import EventToc, { type TocItem } from "@/pages/events/components/EventToc";
import { formatDate, getEventBySlug, getRelatedEvents } from "@/lib/events";
import { siteInfo } from "@/mocks/site";

const TOC_ITEMS: TocItem[] = [
  { id: "timeline", label: "完整经过" },
  { id: "figures", label: "关键人物" },
  { id: "controversies", label: "争议焦点" },
  { id: "analysis", label: "深度解析" },
  { id: "takeaway", label: "复盘结论" },
];

export default function EventDetail() {
  const { slug } = useParams<{ slug: string }>();
  const event = slug ? getEventBySlug(slug) : undefined;

  useDocumentMeta({
    title: event
      ? `${event.title}｜完整经过与深度解析 - 吃瓜复盘局`
      : "事件未找到 - 吃瓜复盘局",
    description: event
      ? event.summary
      : "该事件复盘可能已调整地址，请前往事件库浏览其他网红事件完整经过与深度解析。",
    keywords: event ? event.tags.join(",") : "网红事件,事件复盘",
    canonical: `${siteInfo.domain}/events/${slug ?? ""}`,
    image: event?.cover,
    type: "article",
    jsonLd: event
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: event.title,
            description: event.summary,
            image: event.cover,
            datePublished: event.publishedAt,
            dateModified: event.updatedAt,
            articleSection: event.categoryName,
            inLanguage: "zh-CN",
            keywords: event.tags.join(","),
            author: { "@type": "Organization", name: siteInfo.name },
            publisher: { "@type": "Organization", name: siteInfo.name },
            mainEntityOfPage: `${siteInfo.domain}/events/${event.slug}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: `${siteInfo.domain}/` },
              { "@type": "ListItem", position: 2, name: "事件库", item: `${siteInfo.domain}/events` },
              {
                "@type": "ListItem",
                position: 3,
                name: event.title,
                item: `${siteInfo.domain}/events/${event.slug}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!event) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center md:px-8">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-500">
          <i className="ri-file-search-line text-3xl"></i>
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">
          没有找到这篇复盘
        </h1>
        <p className="mt-3 text-sm leading-7 text-foreground-600">
          链接可能已经更新。你可以回到事件库，按分类或关键词找到想看的事件完整经过。
        </p>
        <Link
          to="/events"
          className="mt-7 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回事件库
          <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    );
  }

  const related = getRelatedEvents(event, 3);

  return (
    <div className="bg-background-50">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-10">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: "事件库", href: "/events" },
              { label: event.categoryName, href: `/events?category=${event.categorySlug}` },
              { label: event.title },
            ]}
          />
          <div className="mt-5 flex flex-wrap items-center gap-3 text-xs">
            <Link
              to={`/events?category=${event.categorySlug}`}
              className="rounded-full bg-primary-100 px-3 py-1 font-medium text-primary-700 transition-colors hover:bg-primary-200"
            >
              {event.categoryName}
            </Link>
            <span className="rounded-full bg-secondary-100 px-3 py-1 font-medium text-secondary-900">
              {event.status}
            </span>
            <span className="text-foreground-500">更新于 {formatDate(event.updatedAt)}</span>
          </div>
          <h1 className="mt-4 max-w-4xl font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            {event.title}
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-foreground-600 md:text-base">
            {event.summary}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              发布 {formatDate(event.publishedAt)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              阅读约 {event.readTime} 分钟
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-fire-line"></i>
              热度 {event.heat}
            </span>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-8 md:py-14">
        <div className="grid gap-10 lg:grid-cols-12">
          <article className="lg:col-span-8">
            <div className="h-60 w-full overflow-hidden rounded-lg bg-background-200 md:h-80">
              <img
                src={event.cover}
                alt={`${event.title} 复盘配图`}
                title={`${event.title} - 网红事件完整经过`}
                className="h-full w-full object-cover"
              />
            </div>

            <section id="timeline" className="scroll-mt-24 pt-12">
              <h2 className="flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-sm text-background-50">
                  一
                </span>
                完整经过
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                按时间顺序还原事件全貌，每个节点都标注了当天的关键动作与公开表态。
              </p>
              <ol className="mt-7 border-l border-background-300 pl-6">
                {event.timeline.map((item) => (
                  <li key={item.date} className="relative pb-8 last:pb-0">
                    <span className="absolute -left-[31px] top-1.5 flex h-3 w-3 items-center justify-center">
                      <span className="h-3 w-3 rounded-full border-2 border-primary-500 bg-background-50"></span>
                    </span>
                    <p className="text-xs font-medium text-primary-600">{item.date}</p>
                    <h3 className="mt-1.5 font-heading text-base font-bold text-foreground-950">
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm leading-7 text-foreground-600">{item.desc}</p>
                  </li>
                ))}
              </ol>
            </section>

            <section id="figures" className="scroll-mt-24 pt-12">
              <h2 className="flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-sm text-background-50">
                  二
                </span>
                关键人物
              </h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {event.figures.map((figure) => (
                  <div
                    key={figure.name}
                    className="rounded-lg border border-background-200 bg-background-100 p-5"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-500 font-heading text-sm font-bold text-background-50">
                        {figure.name.slice(0, 1)}
                      </span>
                      <div>
                        <p className="font-heading text-sm font-bold text-foreground-950">
                          {figure.name}
                        </p>
                        <p className="text-xs text-foreground-500">{figure.role}</p>
                      </div>
                    </div>
                    <p className="mt-3 text-sm leading-7 text-foreground-600">{figure.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section id="controversies" className="scroll-mt-24 pt-12">
              <h2 className="flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-sm text-background-50">
                  三
                </span>
                争议焦点
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                把舆论中分歧最大的问题拆开，先看事实层面能确认什么，再看观点层面的差异在哪。
              </p>
              <div className="mt-6 space-y-4">
                {event.controversies.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-lg border border-background-200 border-l-4 border-l-accent-500 bg-background-50 p-5 md:p-6"
                  >
                    <h3 className="font-heading text-base font-bold text-foreground-950">
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm font-medium text-accent-800">{item.question}</p>
                    <p className="mt-3 text-sm leading-7 text-foreground-600">{item.analysis}</p>
                  </div>
                ))}
              </div>
            </section>

            <section id="analysis" className="scroll-mt-24 pt-12">
              <h2 className="flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-sm text-background-50">
                  四
                </span>
                深度解析
              </h2>
              <div className="mt-6 space-y-7">
                {event.analyses.map((item) => (
                  <div key={item.heading}>
                    <h3 className="font-heading text-lg font-bold text-foreground-950">
                      {item.heading}
                    </h3>
                    <p className="mt-2 text-sm leading-7 text-foreground-600 md:text-[15px]">
                      {item.body}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section id="takeaway" className="scroll-mt-24 pt-12">
              <h2 className="flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-sm text-background-50">
                  五
                </span>
                复盘结论
              </h2>
              <div className="mt-6 rounded-lg bg-secondary-50 p-6 md:p-7">
                <i className="ri-lightbulb-flash-line text-2xl text-secondary-600"></i>
                <p className="mt-3 font-heading text-base font-bold leading-7 text-foreground-950 md:text-lg">
                  {event.takeaway}
                </p>
              </div>
            </section>

            <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100 p-5">
              <p className="flex items-start gap-2 text-xs leading-6 text-foreground-500">
                <i className="ri-information-line mt-0.5"></i>
                信息来源说明：本文基于当事方公开表态、平台公告与主流媒体报道整理，涉及个人隐私的信息已作脱敏处理。若发现内容有误，欢迎通过邮件指出，我们核实后会公开更正。
              </p>
            </div>
          </article>

          <aside className="lg:col-span-4">
            <div className="space-y-6 lg:sticky lg:top-24">
              <EventToc items={TOC_ITEMS} />
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">事件标签</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {event.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/events?q=${encodeURIComponent(tag)}`}
                      className="rounded-full bg-background-200 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-accent-100 hover:text-accent-900"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>
              <div className="rounded-lg bg-primary-50 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">
                  想持续跟这个事件？
                </h2>
                <p className="mt-2 text-xs leading-6 text-foreground-600">
                  在首页订阅事件更新，出现后续进展时我们会把摘要发到你的邮箱。
                </p>
                <Link
                  to="/#subscribe"
                  className="mt-4 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  订阅更新
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-16 border-t border-background-200 pt-12">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            相关复盘推荐
          </h2>
          <p className="mt-2 text-sm text-foreground-600">
            发酵路径相似或信息结构接近的事件，放在一起看会更清楚。
          </p>
          <div className="mt-7 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((item) => (
              <EventCard key={item.slug} event={item} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}